import type { LandmarkCategory } from "../landmarks";

// Photo check-ins + landmark discovery (spec 08-landmarks LMK-9/LMK-10).
// Response shapes mirror packages/api/src/routes/landmarks.ts exactly.

export interface CheckinExplorer {
  username: string | null;
  avatarUrl: string | null;
  /** False for private profiles — render handle-only, no /explorers link. */
  profilePublic: boolean;
}

export interface LandmarkCheckin {
  id: string;
  /** Null for a summit check-in (spec 34). */
  landmarkId: string | null;
  /** The peak this check-in collected, if any (summit check-in, or a trig
   * pillar photographed within PEAK_CHECKIN_RADIUS_M of its summit). */
  peakId: string | null;
  photoUrl: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  checkedInAt: string;
  distanceM: number | null;
  locationWarning: boolean;
  source: "web" | "mobile";
  isPrivate: boolean;
  moderationStatus: "approved" | "hidden";
  createdAt: string;
}

/** The owner's equipped check-in stamp (store `stamp` cosmetic), if any. */
export interface CheckinStamp {
  slug: string;
  name: string;
  assetKey: string;
}

export interface GalleryCheckin extends LandmarkCheckin {
  isOwn: boolean;
  stamp: CheckinStamp | null;
  explorer: CheckinExplorer;
}

export interface LandmarkCheckinsResponse {
  checkins: GalleryCheckin[];
  nextCursor: string | null;
}

export interface CheckinUploadUrlResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}

export interface CreateCheckinRequest {
  /** Client-generated idempotency key (uuid) — set before the first attempt. */
  clientId: string;
  photoKey: string;
  width?: number;
  height?: number;
  caption?: string;
  /** Capture time (EXIF DateTimeOriginal or device clock), ISO-8601. */
  checkedInAt: string;
  deviceLat?: number;
  deviceLon?: number;
  deviceAccuracyM?: number;
  exifLat?: number;
  exifLon?: number;
  /** Start the check-in private (bulk imports default to this, spec 39). */
  isPrivate?: boolean;
  /** Bulk import batch (spec 39): the server skips the account steps until
   * POST /landmarks/checkins/finish. */
  batchId?: string;
}

export interface CreateCheckinResponse {
  checkin: LandmarkCheckin;
  /** True when the photo doesn't look like it was taken here — never blocks. */
  warning: boolean;
  distanceM: number | null;
  /** Whether this check-in created the user_landmark_log tick. */
  firstVisit: boolean;
  /** True when the same clientId had already been accepted (outbox retry). */
  deduped: boolean;
  /** The peak this check-in collected (spec 34), with whether it was new. */
  peakCredited: { slug: string; name: string; firstVisit: boolean } | null;
}

/**
 * Codes on a 422 from POST /peaks/:slug/checkins. Final: the mobile outbox
 * never retries them.
 */
export type SummitCheckinRefusalCode = "no_location" | "too_far" | "history_locked";
export const SUMMIT_CHECKIN_REFUSAL_CODES: readonly SummitCheckinRefusalCode[] = [
  "no_location",
  "too_far",
  "history_locked",
];

export interface NearbyLandmark {
  id: string;
  slug: string;
  name: string;
  category: LandmarkCategory;
  latitude: number;
  longitude: number;
  distanceM: number;
  alreadyVisited: boolean;
}

export interface NearbyLandmarksResponse {
  landmarks: NearbyLandmark[];
}

/** Lightweight reference row powering the mobile on-device index (LMK-13a). */
export interface LandmarkIndexEntry {
  id: string;
  slug: string;
  name: string;
  category: LandmarkCategory;
  latitude: number;
  longitude: number;
}

export interface LandmarkIndexResponse {
  landmarks: LandmarkIndexEntry[];
  fetchedAt: string;
}
